import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Check, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { api } from '../lib/api';
import t from '../translations';

export default function PricingPage() {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const { lang } = useLang();
  const tr = t[lang];

  const [plan, setPlan]     = useState('yearly');
  const [promoCode, setPromoCode] = useState('');
  const [promoMsg, setPromoMsg]   = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoMsg(null);
    try {
      const d = await api.get(`/api/payment/promo/validate?code=${promoCode.trim()}`);
      setPromoMsg({ ok: true, text: d.description, applied: d.applied });
    } catch (err) {
      setPromoMsg({ ok: false, text: err.message || 'Invalid code' });
    }
    setPromoLoading(false);
  };

  const handleSubscribe = async () => {
    if (!user) { navigate('/auth'); return; }
    setLoading(true);
    try {
      const order = await api.post('/api/payment/create-order', {
        plan,
        promoCode: promoCode.trim() || null,
      });
      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'DailyRaashi',
        description: plan === 'yearly' ? '1 Year Subscription' : '1 Month Subscription',
        order_id: order.orderId,
        handler: async (response) => {
          try {
            await api.post('/api/payment/verify', {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              plan,
            });
            navigate('/home');
          } catch {
            alert(tr.paymentFailed);
          }
        },
        prefill: { name: profile?.name || '', email: profile?.email || '' },
        theme: { color: '#C9A84C' },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      alert(tr.paymentFailed);
    }
    setLoading(false);
  };

  const features = [
    'Daily personalised Vedic reading',
    'Colour, food & routine guidance',
    'Dasha-aware predictions from Jyoti',
    'Today\'s Muhurtas & Chaughadiyas',
    'Cosmic planetary snapshot',
    'Vedic baby name generator',
    'Hindi & English support',
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0A0F1E', paddingBottom: 48 }}>

      {/* Header */}
      <div style={{ padding: '56px 20px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
        >
          <ChevronLeft size={22} />
        </button>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 700 }}>
            {tr.pricingTitle}
          </h1>
          <p style={{ fontSize: 13, color: '#8A8580', marginTop: 2 }}>{tr.pricingSubtitle}</p>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>

        {/* Plan Selector */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {/* Yearly */}
          <button
            data-testid="pricing-yearly-btn"
            onClick={() => setPlan('yearly')}
            style={{
              flex: 1, padding: '20px 16px', borderRadius: 16, cursor: 'pointer',
              border: `2px solid ${plan === 'yearly' ? '#C9A84C' : 'rgba(255,255,255,0.08)'}`,
              background: plan === 'yearly' ? 'rgba(201,168,76,0.08)' : '#111827',
              textAlign: 'left', position: 'relative', overflow: 'hidden',
            }}
          >
            {plan === 'yearly' && (
              <div style={{
                position: 'absolute', top: 10, right: 10,
                background: '#C9A84C', color: '#0A0F1E',
                fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                padding: '3px 8px', borderRadius: 8, textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <Star size={8} fill="#0A0F1E" /> {tr.bestValue}
              </div>
            )}
            <p style={{ fontSize: 12, color: plan === 'yearly' ? '#C9A84C' : '#8A8580', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
              {tr.yearlyPlan}
            </p>
            <p style={{ fontSize: 26, fontWeight: 700, color: '#F5F0E8', fontFamily: "'Playfair Display', serif", marginBottom: 2 }}>
              ₹499
            </p>
            <p style={{ fontSize: 11, color: '#8A8580' }}>per year · ₹41/mo</p>
            <p style={{ fontSize: 10, color: '#2D6A4F', marginTop: 4, fontWeight: 600 }}>{tr.saveLabel}</p>
          </button>

          {/* Monthly */}
          <button
            data-testid="pricing-monthly-btn"
            onClick={() => setPlan('monthly')}
            style={{
              flex: 1, padding: '20px 16px', borderRadius: 16, cursor: 'pointer',
              border: `2px solid ${plan === 'monthly' ? '#C9A84C' : 'rgba(255,255,255,0.08)'}`,
              background: plan === 'monthly' ? 'rgba(201,168,76,0.08)' : '#111827',
              textAlign: 'left',
            }}
          >
            <p style={{ fontSize: 12, color: plan === 'monthly' ? '#C9A84C' : '#8A8580', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
              {tr.monthlyPlan}
            </p>
            <p style={{ fontSize: 26, fontWeight: 700, color: '#F5F0E8', fontFamily: "'Playfair Display', serif", marginBottom: 2 }}>
              ₹49
            </p>
            <p style={{ fontSize: 11, color: '#8A8580' }}>per month</p>
          </button>
        </div>

        {/* Features */}
        <div className="card" style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 12, color: '#C9A84C', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
            Everything Included
          </p>
          {features.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < features.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
              <div style={{
                width: 20, height: 20, borderRadius: 6,
                background: 'rgba(45,106,79,0.2)', border: '1px solid rgba(45,106,79,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Check size={12} color="#2D6A4F" />
              </div>
              <p style={{ fontSize: 13, color: '#F5F0E8' }}>{f}</p>
            </div>
          ))}
        </div>

        {/* Promo Code */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              data-testid="promo-input"
              type="text"
              className="input-field"
              placeholder="Promo code"
              value={promoCode}
              onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoMsg(null); }}
              style={{ flex: 1, letterSpacing: '0.08em' }}
            />
            <button
              data-testid="apply-promo-btn"
              onClick={validatePromo}
              disabled={promoLoading || !promoCode.trim()}
              style={{
                padding: '0 18px', background: 'rgba(201,168,76,0.1)',
                border: '1px solid rgba(201,168,76,0.3)', borderRadius: 12,
                color: '#C9A84C', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                opacity: !promoCode.trim() ? 0.5 : 1, flexShrink: 0,
              }}
            >
              {promoLoading ? '…' : 'Apply'}
            </button>
          </div>
          {promoMsg && (
            <p style={{ fontSize: 12, marginTop: 6, color: promoMsg.ok ? '#2D6A4F' : '#C0392B' }}>
              {promoMsg.ok ? '✓' : '✗'} {promoMsg.text}
            </p>
          )}
        </div>

        {/* Subscribe CTA */}
        <button
          data-testid="subscribe-btn"
          onClick={handleSubscribe}
          disabled={loading}
          className="btn-primary pulse-gold"
          style={{ fontSize: 16, padding: '18px 28px' }}
        >
          {loading ? tr.loading : `${tr.subscribeNow} — ₹${plan === 'yearly' ? '499/yr' : '49/mo'}`}
        </button>

        <p style={{ fontSize: 11, color: '#8A8580', textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
          Secure payment via Razorpay. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
